import React, { useState } from 'react';
import styled from 'styled-components';
import { User, Class } from '../types/alarm';
import ProfileEditModal from '../components/ProfileEditModal';
import Header from '../components/Header';
import { hideScrollbar } from '../styles/hideScrollbar';
// 불필요한 import 제거 - App.tsx에서 클래스 데이터 관리

interface HomePageProps {
  onNavigate: (page: 'home' | 'alarm' | 'admin' | 'assignments' | 'points-table' | 'chat' | 'board' | 'license' | 'terms-of-service' | 'privacy-policy') => void;
  user: User;
  onLogout: () => void;
  onUpdateUser: (updatedUser: User) => void;
  classes: Class[];
  currentClass: Class | null;
  memberGroups?: string[];
  currentMemberGroup?: string;
  onMemberGroupChange?: (group: string) => void;
  onClassManageModalOpen: () => void;
  /** 현재 선택된 클래스의 클래스 설정 모달을 바로 열기 */
  onClassSettingsDirectClick?: () => void;
  // 포인트 메뉴 핸들러들
  onSeatAssignmentClick?: () => void;
  onLevelSettingsClick?: () => void;
  onPointManagementClick?: () => void;
  onItemCardSettingsClick?: () => void;
  onMemberProfileSettingsClick?: () => void;
  onAssignmentSettingsClick?: () => void;
  onProfileModalOpen: () => void;
  themeMode: 'dark' | 'light';
  onToggleTheme: () => void;
  onAnnouncementOpen?: () => void;
}

const HomePage: React.FC<HomePageProps> = ({ 
  onNavigate, 
  user, 
  onLogout, 
  onUpdateUser, 
  classes, 
  currentClass, 
  memberGroups,
  currentMemberGroup,
  onMemberGroupChange,
  onClassManageModalOpen, 
  onClassSettingsDirectClick,
  onSeatAssignmentClick, 
  onLevelSettingsClick, 
  onPointManagementClick, 
  onItemCardSettingsClick, 
  onMemberProfileSettingsClick,
  onAssignmentSettingsClick, 
  onProfileModalOpen,
  themeMode,
  onToggleTheme,
  onAnnouncementOpen
}) => {
  const [showProfileModal, setShowProfileModal] = useState(false);

  // useEffect 제거 - App.tsx에서 클래스 데이터 관리

  const handleLogout = () => {
    // App.tsx의 handleLogout에서 Firebase 로그아웃을 처리하므로 여기서는 onLogout만 호출
    onLogout();
  };

  const handleProfileClick = () => {
    setShowProfileModal(true);
  };

  const handleProfileSave = (updatedUser: User) => {
    onUpdateUser(updatedUser);
    setShowProfileModal(false);
  };

  const handleProfileCancel = () => {
    setShowProfileModal(false);
  };

  // handleClassClick, handleClassChange 제거 - App.tsx에서 처리

  return (
    <HomeContainer>
      <Header 
        user={user} 
        onLogout={onLogout} 
        onNavigate={onNavigate}
        currentPage="home"
        onUpdateUser={onUpdateUser}
        onProfileModalOpen={handleProfileClick}
        onClassManageModalOpen={onClassManageModalOpen}
        classes={classes}
        currentClass={currentClass}
        memberGroups={memberGroups}
        currentMemberGroup={currentMemberGroup}
        onMemberGroupChange={onMemberGroupChange}
        themeMode={themeMode}
        onToggleTheme={onToggleTheme}
        onSeatAssignmentClick={onSeatAssignmentClick}
        onLevelSettingsClick={onLevelSettingsClick}
        onPointManagementClick={onPointManagementClick}
        onItemCardSettingsClick={onItemCardSettingsClick}
        onMemberProfileSettingsClick={onMemberProfileSettingsClick}
        onAssignmentSettingsClick={onAssignmentSettingsClick}
      />
      
      <MainSection>
        {/* 상단 중앙: 클래스/그룹 설정 원형 아이콘 버튼 (선택된 클래스가 있을 때만) */}
        {currentClass && (onClassSettingsDirectClick || onAssignmentSettingsClick || onMemberProfileSettingsClick || onSeatAssignmentClick || onPointManagementClick || onItemCardSettingsClick || onLevelSettingsClick) && (
        <TopSettingsBar>
          <SettingsTooltipWrapper data-tooltip="클래스 설정">
            <TopSettingsIconButton
              type="button"
              onClick={onClassSettingsDirectClick}
              disabled={!currentClass || !onClassSettingsDirectClick}
              aria-label="클래스 설정"
            >
              <img src="/clas_setting.svg" alt="" />
            </TopSettingsIconButton>
          </SettingsTooltipWrapper>
          <SettingsTooltipWrapper data-tooltip="기본 환경설정">
            <TopSettingsIconButton
              type="button"
              onClick={onAssignmentSettingsClick}
              disabled={!currentClass || !onAssignmentSettingsClick}
              aria-label="기본 환경설정"
            >
              <img src="/basic_setting.svg" alt="" />
            </TopSettingsIconButton>
          </SettingsTooltipWrapper>
          {onMemberProfileSettingsClick && (
            <SettingsTooltipWrapper data-tooltip="사진 프로필 설정">
              <TopSettingsIconButton
                type="button"
                onClick={onMemberProfileSettingsClick}
                disabled={!currentClass}
                aria-label="사진 프로필 설정"
              >
                <img src="/picprofile.svg" alt="" />
              </TopSettingsIconButton>
            </SettingsTooltipWrapper>
          )}
          {onSeatAssignmentClick && (
            <SettingsTooltipWrapper data-tooltip="자리배치">
              <TopSettingsIconButton
                type="button"
                onClick={onSeatAssignmentClick}
                disabled={!currentClass}
                aria-label="자리배치"
              >
                <img src="/seatarrangement.svg?v=2" alt="" />
              </TopSettingsIconButton>
            </SettingsTooltipWrapper>
          )}
          {onPointManagementClick && (
            <SettingsTooltipWrapper data-tooltip="포인트 세트 설정">
              <TopSettingsIconButton
                type="button"
                onClick={onPointManagementClick}
                disabled={!currentClass}
                aria-label="포인트 세트 설정"
              >
                <img src="/preset.svg" alt="" />
              </TopSettingsIconButton>
            </SettingsTooltipWrapper>
          )}
          {onItemCardSettingsClick && (
            <SettingsTooltipWrapper data-tooltip="아이템 카드 설정">
              <TopSettingsIconButton
                type="button"
                onClick={onItemCardSettingsClick}
                disabled={!currentClass}
                aria-label="아이템 카드 설정"
              >
                <img src="/itemcard.svg" alt="" />
              </TopSettingsIconButton>
            </SettingsTooltipWrapper>
          )}
          {onLevelSettingsClick && (
            <SettingsTooltipWrapper data-tooltip="레벨 시스템 설정">
              <TopSettingsIconButton
                type="button"
                onClick={onLevelSettingsClick}
                disabled={!currentClass}
                aria-label="레벨 시스템 설정"
              >
                <img src="/Level.svg" alt="" />
              </TopSettingsIconButton>
            </SettingsTooltipWrapper>
          )}
        </TopSettingsBar>
        )}

        <MainButtonsContainer>
          <MainButton onClick={() => onNavigate('alarm')}>
            <MainButtonIcon>
              <img src="/alram.svg" alt="알람" />
            </MainButtonIcon>
            <MainButtonTitle>활동 알람</MainButtonTitle>
          </MainButton>
          <MainButton onClick={() => onNavigate('assignments')}>
            <MainButtonIcon>
              <img src="/task.svg" alt="과제" />
            </MainButtonIcon>
            <MainButtonTitle>과제 관리</MainButtonTitle>
          </MainButton>
          <MainButton onClick={() => onNavigate('points-table')}>
            <MainButtonIcon>
              <img src="/point.svg" alt="포인트" />
            </MainButtonIcon>
            <MainButtonTitle>포인트 코인</MainButtonTitle>
          </MainButton>
          <MainButton onClick={() => onNavigate('chat')}>
            <MainButtonIcon>
              <img src="/Chat.svg" alt="챗" />
            </MainButtonIcon>
            <MainButtonTitle>클래스 챗</MainButtonTitle>
          </MainButton>
          <MainButton onClick={() => onNavigate('board')}>
            <MainButtonIcon>
              <img src="/blackboard.svg" alt="보드" />
            </MainButtonIcon>
            <MainButtonTitle>클래스 보드</MainButtonTitle>
          </MainButton>
          {user.userType === 'admin' && (
            <AdminButton onClick={() => onNavigate('admin')}>
              <MainButtonIcon>
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M19.4 15C19.2669 15.3016 19.2272 15.6362 19.286 15.9606C19.3448 16.285 19.4995 16.5843 19.73 16.82L19.79 16.88C19.976 17.0657 20.1235 17.2863 20.2241 17.5291C20.3248 17.7719 20.3766 18.0322 20.3766 18.295C20.3766 18.5578 20.3248 18.8181 20.2241 19.0609C20.1235 19.3037 19.976 19.5243 19.79 19.71C19.6043 19.896 19.3837 20.0435 19.1409 20.1441C18.8981 20.2448 18.6378 20.2966 18.375 20.2966C18.1122 20.2966 17.8519 20.2448 17.6091 20.1441C17.3663 20.0435 17.1457 19.896 16.96 19.71L16.9 19.65C16.6643 19.4195 16.365 19.2648 16.0406 19.206C15.7162 19.1472 15.3816 19.1869 15.08 19.32C14.7842 19.4468 14.532 19.6572 14.3543 19.9255C14.1766 20.1938 14.0813 20.5082 14.08 20.83V21C14.08 21.5304 13.8693 22.0391 13.4942 22.4142C13.1191 22.7893 12.6104 23 12.08 23C11.5496 23 11.0409 22.7893 10.6658 22.4142C10.2907 22.0391 10.08 21.5304 10.08 21V20.91C10.0723 20.579 9.96512 20.258 9.77251 19.9887C9.5799 19.7194 9.31074 19.5143 9 19.4C8.69838 19.2669 8.36381 19.2272 8.03941 19.286C7.71502 19.3448 7.41568 19.4995 7.18 19.73L7.12 19.79C6.93425 19.976 6.71368 20.1235 6.47088 20.2241C6.22808 20.3248 5.96783 20.3766 5.705 20.3766C5.44217 20.3766 5.18192 20.3248 4.93912 20.2241C4.69632 20.1235 4.47575 19.976 4.29 19.79C4.10405 19.6043 3.95653 19.3837 3.85588 19.1409C3.75523 18.8981 3.70343 18.6378 3.70343 18.375C3.70343 18.1122 3.75523 17.8519 3.85588 17.6091C3.95653 17.3663 4.10405 17.1457 4.29 16.96L4.35 16.9C4.58054 16.6643 4.73519 16.365 4.794 16.0406C4.85282 15.7162 4.81312 15.3816 4.68 15.08C4.55324 14.7842 4.34276 14.532 4.07447 14.3543C3.80618 14.1766 3.49179 14.0813 3.17 14.08H3C2.46957 14.08 1.96086 13.8693 1.58579 13.4942C1.21071 13.1191 1 12.6104 1 12.08C1 11.5496 1.21071 11.0409 1.58579 10.6658C1.96086 10.2907 2.46957 10.08 3 10.08H3.09C3.42099 10.0723 3.742 9.96512 4.01131 9.77251C4.28062 9.5799 4.48571 9.31074 4.6 9C4.73312 8.69838 4.77282 8.36381 4.714 8.03941C4.65519 7.71502 4.50054 7.41568 4.27 7.18L4.21 7.12C4.02405 6.93425 3.87653 6.71368 3.77588 6.47088C3.67523 6.22808 3.62343 5.96783 3.62343 5.705C3.62343 5.44217 3.67523 5.18192 3.77588 4.93912C3.87653 4.69632 4.02405 4.47575 4.21 4.29C4.39575 4.10405 4.61632 3.95653 4.85912 3.85588C5.10192 3.75523 5.36217 3.70343 5.625 3.70343C5.88783 3.70343 6.14808 3.75523 6.39088 3.85588C6.63368 3.95653 6.85425 4.10405 7.04 4.29L7.1 4.35C7.33568 4.58054 7.63502 4.73519 7.95941 4.794C8.28381 4.85282 8.61838 4.81312 8.92 4.68H9C9.29577 4.55324 9.54802 4.34276 9.72569 4.07447C9.90337 3.80618 9.99872 3.49179 10 3.17V3C10 2.46957 10.2107 1.96086 10.5858 1.58579C10.9609 1.21071 11.4696 1 12 1C12.5304 1 13.0391 1.21071 13.4142 1.58579C13.7893 1.96086 14 2.46957 14 3V3.09C14.0013 3.41179 14.0966 3.72618 14.2743 3.99447C14.452 4.26276 14.7042 4.47324 15 4.6C15.3016 4.73312 15.6362 4.77282 15.9606 4.714C16.285 4.65519 16.5843 4.50054 16.82 4.27L16.88 4.21C17.0657 4.02405 17.2863 3.87653 17.5291 3.77588C17.7719 3.67523 18.0322 3.62343 18.295 3.62343C18.5578 3.62343 18.8181 3.67523 19.0609 3.77588C19.3037 3.87653 19.5243 4.02405 19.71 4.21C19.896 4.39575 20.0435 4.61632 20.1441 4.85912C20.2448 5.10192 20.2966 5.36217 20.2966 5.625C20.2966 5.88783 20.2448 6.14808 20.1441 6.39088C20.0435 6.63368 19.896 6.85425 19.71 7.04L19.65 7.1C19.4195 7.33568 19.2648 7.63502 19.206 7.95941C19.1472 8.28381 19.1869 8.61838 19.32 8.92V9C19.4468 9.29577 19.6572 9.54802 19.9255 9.72569C20.1938 9.90337 20.5082 9.99872 20.83 10H21C21.5304 10 22.0391 10.2107 22.4142 10.5858C22.7893 10.9609 23 11.4696 23 12C23 12.5304 22.7893 13.0391 22.4142 13.4142C22.0391 13.7893 21.5304 14 21 14H20.91C20.5882 14.0013 20.2738 14.0966 20.0055 14.2743C19.7372 14.452 19.5268 14.7042 19.4 15Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </MainButtonIcon>
              <MainButtonTitle>관리자 페이지</MainButtonTitle>
            </AdminButton>
          )}
          {onAnnouncementOpen && (
            <AnnouncementButton onClick={onAnnouncementOpen} title="팝업 공지">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M13.73 21a2 2 0 0 1-3.46 0" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </AnnouncementButton>
          )}
          <LicensePillRow $afterAnnouncement={!!onAnnouncementOpen}>
            <LicensePillButton
              type="button"
              onClick={() => onNavigate('license')}
              aria-label="이용권 구매 및 관리"
            >
              이용권 구매 및 관리
            </LicensePillButton>
          </LicensePillRow>
          {/* EDUKODI 로고 (종모양 공지 버튼 아래 100px 여백) */}
          <HomePageBottomLogoWrap>
            <img src="/EDUKODI_LOGO2.svg?v=2" alt="" aria-hidden />
          </HomePageBottomLogoWrap>
        </MainButtonsContainer>
        
        {/* 회사 정보 섹션 */}
        <CompanyInfoSection>
          <CompanyInfoContent>
            <CompanyLogo>
              <img src="/LOGOeducodi_new.svg" alt="에듀코디" />
            </CompanyLogo>
            <CompanyInfoText>
              <div>제이앤조이 대표자 : 정영은</div>
              <div>통신판매업신고번호 : 2024-화성동탄-0316호</div>
              <div>사업자등록번호 : 702-04-02761</div>
              <div>주소 : 경기도 화성시 동탄대로시범길 20. 1415동 2701호</div>
              <PolicyLinks>
                <PolicyLink onClick={() => onNavigate('terms-of-service')}>[서비스 이용약관]</PolicyLink>
                <PolicyLink onClick={() => onNavigate('privacy-policy')}>[개인정보처리방침]</PolicyLink>
              </PolicyLinks>
              <div>연락처 : 010-7442-0199</div>
              <div>결제 및 상담 : luckytem_mk@naver.com</div>
            </CompanyInfoText>
          </CompanyInfoContent>
        </CompanyInfoSection>
      </MainSection>

      <ProfileEditModal
        isOpen={showProfileModal}
        user={user}
        onSave={handleProfileSave}
        onCancel={handleProfileCancel}
        onLogout={handleLogout}
      />
    </HomeContainer>
  );
};

const HomeContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--color-home-bg);
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const MainSection = styled.div`
  position: absolute;
  top: 44px; /* 헤더 높이 (36px + 여백) */
  left: 0;
  right: 0;
  bottom: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 20px 20px 0 20px;
  z-index: 1; /* 헤더(z-index: 1000)보다 낮게 */
  display: flex;
  flex-direction: column;
  ${hideScrollbar}
`;

/* 상단 중앙: 원형 아이콘 버튼 바 — 가로 400px 미만이면 1행 3개, 2행 2개 */
const TopSettingsBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  gap: 16px;
  padding: 12px 16px 16px;
  max-width: 100%;
  margin: 0 auto;
  box-sizing: border-box;

  @media (max-width: 399px) {
    /* box-sizing: border-box이므로 패딩(좌우 16*2) 포함해 208px → 내용 176px에 3개+2개 */
    width: 208px;
  }
`;

const SettingsTooltipWrapper = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  &[data-tooltip]::after,
  &[data-tooltip]::before {
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.1s ease;
  }

  &[data-tooltip]::after {
    content: attr(data-tooltip);
    position: absolute;
    bottom: calc(100% + 4px);
    left: 50%;
    transform: translateX(-50%);
    background: rgba(var(--color-accent-rgb), 0.92);
    color: #fff;
    font-size: 11px;
    font-weight: 500;
    padding: 4px 8px;
    border-radius: 6px;
    white-space: nowrap;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    z-index: 10;
  }

  &[data-tooltip]::before {
    content: '';
    position: absolute;
    bottom: calc(100% + 3px);
    left: 50%;
    transform: translateX(-50%);
    border-left: 6px solid transparent;
    border-right: 6px solid transparent;
    border-top: 6px solid rgba(var(--color-accent-rgb), 0.92);
    z-index: 9;
  }

  &:hover::after,
  &:hover::before {
    opacity: 1;
  }
`;

const TopSettingsIconButton = styled.button`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.1);
    border-color: rgba(255, 255, 255, 0.5);
    transform: scale(1.05);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }

  &:focus-visible {
    outline: 2px solid rgba(255, 255, 255, 0.8);
    outline-offset: 2px;
  }

  img {
    width: 26px;
    height: 26px;
    filter: brightness(0) invert(1); /* 흰색 아이콘 */
  }
`;

/* 종모양(공지) 버튼 아래 100px 여백 후 하단 로고가 오도록 padding-bottom 사용 */
const MainButtonsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  max-width: 600px;
  margin: 0 auto;
  padding: 0 20px 100px 20px;
  box-sizing: border-box;
  
  @media (max-width: 768px) {
    gap: 10px;
    padding: 0 20px 100px 20px;
  }
  
  @media (max-width: 480px) {
    gap: 8px;
    padding: 0 16px 100px 16px;
  }
`;

const MainButton = styled.button`
  background: var(--color-surface);
  color: var(--color-text-primary);
  border: 2px solid var(--color-border);
  border-radius: 12px;
  padding: 16px 20px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  box-sizing: border-box;
  
  &:hover {
    background: rgba(255, 255, 255, 0.95);
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.15);
    border-color: var(--color-accent-strong);
  }
  
  /* 다크모드 호버 효과 */
  :root:not([data-theme='light']) & {
    &:hover {
      background: rgba(255, 255, 255, 0.1); /* 다크모드에서는 약간 밝게 */
      border-color: var(--color-accent-strong);
    }
  }
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(var(--color-accent-rgb), 0.2);
  }
  
  @media (max-width: 768px) {
    padding: 14px 18px;
    gap: 14px;
  }
  
  @media (max-width: 480px) {
    padding: 12px 16px;
    gap: 12px;
  }
`;

const MainButtonIcon = styled.div`
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0; /* 아이콘 크기 고정 */
  
  img {
    width: 100%;
    height: 100%;
    filter: brightness(0) saturate(100%) invert(25%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(0.4) contrast(1);
    transition: filter 0.2s ease;
  }
  
  svg {
    width: 32px;
    height: 32px;
    color: var(--color-accent-strong);
    transition: color 0.2s ease;
  }
  
  /* 다크모드 아이콘 색상 */
  :root:not([data-theme='light']) & {
    img {
      filter: brightness(0) saturate(100%) invert(100%);
    }
    svg {
      color: #ffffff;
    }
  }
  
  @media (max-width: 768px) {
    width: 28px;
    height: 28px;
    
    svg {
      width: 28px;
      height: 28px;
    }
  }
  
  @media (max-width: 480px) {
    width: 24px;
    height: 24px;
    
    svg {
      width: 24px;
      height: 24px;
    }
  }
`;

const MainButtonTitle = styled.div`
    font-size: 1rem;
  font-weight: 600;
  color: var(--color-text-primary);
  text-align: left;
  flex: 1; /* 남은 공간을 모두 차지 */
  
  @media (max-width: 768px) {
    font-size: 0.95rem;
  }
  
  @media (max-width: 480px) {
    font-size: 0.9rem;
  }
`;

const AdminButton = styled.button`
  background: var(--color-surface);
  color: var(--color-text-primary);
  border: 2px solid var(--color-border);
  border-radius: 12px;
  padding: 16px 20px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  box-sizing: border-box;
  
  &:hover {
    background: rgba(255, 255, 255, 0.95);
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.15);
    border-color: var(--color-accent-strong);
  }
  
  /* 다크모드 호버 효과 */
  :root:not([data-theme='light']) & {
    &:hover {
      background: rgba(255, 255, 255, 0.1); /* 다크모드에서는 약간 밝게 */
      border-color: var(--color-accent-strong);
    }
  }
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(var(--color-accent-rgb), 0.2);
  }
  
  @media (max-width: 768px) {
    padding: 14px 18px;
    gap: 14px;
  }
  
  @media (max-width: 480px) {
    padding: 12px 16px;
    gap: 12px;
  }
`;

const LicensePillRow = styled.div<{ $afterAnnouncement?: boolean }>`
  display: flex;
  justify-content: center;
  width: 100%;
  margin: 0;
  /* 공지 원형 버튼과 이용권 알약 버튼 사이: flex gap(12px)에 더해 추가 여백 */
  margin-top: ${({ $afterAnnouncement }) => ($afterAnnouncement ? '40px' : '0')};
`;

const LicensePillButton = styled.button`
  /* AnnouncementButton과 동일: 투명 배경 + 흰색 반투명 테두리 + 그림자 */
  background: transparent;
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.45);
  border-radius: 9999px;
  padding: 7px 18px;
  min-height: 36px;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  font-size: 0.8125rem;
  font-weight: 600;
  line-height: 1.2;
  text-align: center;
  max-width: calc(100% - 4px);
  box-sizing: border-box;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    border-color: rgba(255, 255, 255, 0.5);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }

  @media (max-width: 800px) {
    padding: 6px 15px;
    min-height: 34px;
    font-size: 0.78rem;
  }

  @media (max-width: 600px) {
    padding: 6px 12px;
    min-height: 32px;
    font-size: 0.75rem;
  }
`;

const HomePageBottomLogoWrap = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 12px;
  margin-bottom: 200px;
  padding: 0 20px;

  img {
    height: 48px;
    width: auto;
    object-fit: contain;
    opacity: 0.4;
  }

  @media (max-width: 480px) {
    margin-top: 12px;
    margin-bottom: 200px;
    img {
      height: 48px;
    }
  }
`;

const CompanyInfoSection = styled.div`
  background: var(--color-panel);
  padding: 16px 20px;
  margin-top: auto;
  width: calc(100% + 40px);
  margin-left: -20px;
  margin-right: -20px;
  box-sizing: border-box;
  
  /* 다크모드에서 배경색 조정 */
  :root:not([data-theme='light']) & {
    background: rgba(0, 0, 0, 0.2);
  }
  
  /* 라이트모드에서 배경색: #10504A보다 조금 어두운 색상 */
  :root[data-theme='light'] & {
    background: #0d453f;
  }
`;

const CompanyInfoContent = styled.div`
  max-width: 600px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 0 20px;
  box-sizing: border-box;
`;

const CompanyLogo = styled.div`
  flex-shrink: 0;
  
  img {
    height: 20px;
    width: auto;
    opacity: 0.8;
  }
  
  @media (max-width: 768px) {
    img {
      height: 18px;
    }
  }
`;

const CompanyInfoText = styled.div`
  font-size: 11px;
  line-height: 1.5;
  color: var(--color-text-secondary);
  opacity: 0.7;
  text-align: center;
  width: 100%;
  
  /* 라이트 모드에서 텍스트를 흰색으로 */
  :root[data-theme='light'] & {
    color: rgba(255, 255, 255, 0.9);
    opacity: 1;
  }
  
  div {
    margin-bottom: 2px;
  }
  
  div:last-child {
    margin-bottom: 0;
  }
  
  @media (max-width: 768px) {
    font-size: 10px;
  }
`;

const PolicyLinks = styled.div`
  display: flex;
  gap: 8px;
  justify-content: center;
  margin: 6px 0;
  
  @media (max-width: 768px) {
    gap: 6px;
    margin: 4px 0;
  }
`;

const PolicyLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  color: var(--color-text-secondary);
  text-decoration: underline;
  opacity: 0.7;
  font-size: 11px;
  cursor: pointer;
  transition: opacity 0.2s;
  
  &:hover {
    opacity: 1;
    color: var(--color-accent-strong);
  }
  
  /* 라이트 모드에서 텍스트를 흰색으로 */
  :root[data-theme='light'] & {
    color: rgba(255, 255, 255, 0.9);
    opacity: 1;
    
    &:hover {
      color: rgba(255, 255, 255, 1);
    }
  }
  
  @media (max-width: 768px) {
    font-size: 10px;
  }
  
  &:focus-visible {
    outline: 2px solid var(--color-accent-strong);
    outline-offset: 2px;
  }
`;

const AnnouncementButton = styled.button`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.45);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  padding: 0;
  align-self: center;
  margin-top: 8px;
  margin-bottom: 0;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    border-color: rgba(255, 255, 255, 0.5);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }

  svg {
    width: 24px;
    height: 24px;
  }

  @media (max-width: 800px) {
    width: 42px;
    height: 42px;
    
    svg {
      width: 22px;
      height: 22px;
    }
  }

  @media (max-width: 600px) {
    width: 40px;
    height: 40px;
    
    svg {
      width: 20px;
      height: 20px;
    }
  }
`;

export default HomePage;